import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Loader2, ArrowLeft } from "lucide-react";

/**
 * LeadFormModal — single-popup lead capture + (optional) OTP step.
 *
 * Design rules baked in (see the accompanying prompt for the rationale):
 *   • Fresh mount on every open — the parent MUST pass `key` bound to
 *     the open-attempt count so React fully remounts the form; that
 *     guarantees zero leftover state from a previous cancelled attempt.
 *   • Inputs are UNCONTROLLED refs — no `value` binding on the phone
 *     field, so we NEVER reformat / trim / re-validate while the user
 *     is typing. The IME never "jumps" on Indic phone keyboards.
 *   • Validation runs ONLY on `blur` (per-field error) and on `submit`
 *     (aggregate error). Nothing is ever shown mid-typing.
 *   • Phone: type="tel", inputMode="numeric" for the numeric keypad,
 *     +91 fixed prefix rendered as a chip OUTSIDE the editable input.
 *   • Email: type="email" for the correct mobile keyboard.
 *   • OTP step lives inside this SAME popup — form data stays alive on
 *     OTP failure so the user never has to retype anything.
 *   • Keyboard-safe on mobile: DialogContent is height-capped and
 *     internally scrollable; iOS + Android keep the focused input in
 *     view because we set overflow-y:auto on the scrollable body.
 */

const InternalLeadForm = ({
  requireEmail,
  customFields,
  primaryColor,
  submitting,
  serverError,
  onSubmit,
  gameType,
}) => {
  // Uncontrolled refs — we read `.value` at submit-time so typing has
  // ZERO React re-render cost and nothing can silently reformat mid-key.
  const nameRef = useRef(null);
  const phoneRef = useRef(null);
  const emailRef = useRef(null);
  const customRefs = useRef({});
  const [consent, setConsent] = useState(false);

  const [errors, setErrors] = useState({});

  const validators = {
    name: (v) => (v.trim().length >= 2 ? "" : "Enter your full name."),
    phone: (v) => {
      const digits = v.replace(/\D/g, "");
      if (digits.length !== 10) return "Enter a 10-digit mobile number.";
      if (!/^[6-9]/.test(digits))
        return "Indian mobile numbers start with 6, 7, 8 or 9.";
      return "";
    },
    email: (v) => {
      if (!requireEmail && !v.trim()) return "";
      return /\S+@\S+\.\S+/.test(v.trim()) ? "" : "Enter a valid email.";
    },
    consent: (v) => (v ? "" : "Consent is required to receive your coupon."),
  };

  const handleBlur = (field) => {
    const ref = { name: nameRef, phone: phoneRef, email: emailRef }[field];
    const raw = ref ? ref.current?.value || "" : "";
    const msg = validators[field] ? validators[field](raw) : "";
    setErrors((e) => ({ ...e, [field]: msg }));
  };

  const submit = (e) => {
    e.preventDefault();
    const raw = {
      name: nameRef.current?.value || "",
      phone: phoneRef.current?.value || "",
      email: emailRef.current?.value || "",
    };
    const nextErrors = {
      name: validators.name(raw.name),
      phone: validators.phone(raw.phone),
      email: validators.email(raw.email),
      consent: validators.consent(consent),
    };
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    const custom = {};
    (customFields || []).forEach((f, i) => {
      const el = customRefs.current[i];
      if (el) custom[f.label] = el.value || "";
    });

    onSubmit({
      name: raw.name.trim(),
      // Canonical E.164 for the backend — digits-only from the input,
      // prefixed with +91. We never reformatted the displayed value.
      phone: `+91${raw.phone.replace(/\D/g, "")}`,
      email: raw.email.trim() || null,
      custom_fields: custom,
    });
  };

  return (
    <form
      onSubmit={submit}
      className="space-y-4 pt-2"
      data-testid="lead-form"
      noValidate
    >
      <div className="space-y-1.5">
        <Label htmlFor="lf-name">Full name</Label>
        <Input
          id="lf-name"
          ref={nameRef}
          autoComplete="name"
          placeholder="Your name"
          onBlur={() => handleBlur("name")}
          data-testid="lead-form-name-input"
        />
        {errors.name && (
          <p className="text-xs text-destructive" data-testid="lead-form-name-error">
            {errors.name}
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="lf-phone">Mobile number</Label>
        <div className="flex items-stretch rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring overflow-hidden">
          <span
            className="px-3 flex items-center gap-1.5 text-sm bg-secondary text-muted-foreground select-none border-r border-input"
            aria-label="Country code"
          >
            <span aria-hidden="true">🇮🇳</span>
            <span className="font-semibold">+91</span>
          </span>
          <Input
            id="lf-phone"
            ref={phoneRef}
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            enterKeyHint="next"
            maxLength={10}
            placeholder="10-digit mobile"
            onBlur={() => handleBlur("phone")}
            className="border-0 focus-visible:ring-0 rounded-none"
            data-testid="lead-form-phone-input"
          />
        </div>
        {errors.phone && (
          <p className="text-xs text-destructive" data-testid="lead-form-phone-error">
            {errors.phone}
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="lf-email">
          Email {requireEmail ? "" : "(optional)"}
        </Label>
        <Input
          id="lf-email"
          ref={emailRef}
          type="email"
          inputMode="email"
          autoComplete="email"
          enterKeyHint="done"
          placeholder="you@example.com"
          onBlur={() => handleBlur("email")}
          data-testid="lead-form-email-input"
        />
        {errors.email && (
          <p className="text-xs text-destructive" data-testid="lead-form-email-error">
            {errors.email}
          </p>
        )}
      </div>

      {(customFields || []).map((f, idx) => (
        <div className="space-y-1.5" key={idx}>
          <Label>{f.label}</Label>
          <Input
            type={f.type === "tel" ? "tel" : f.type === "email" ? "email" : "text"}
            ref={(el) => {
              customRefs.current[idx] = el;
            }}
            data-testid={`lead-form-custom-${idx}`}
          />
        </div>
      ))}

      <label className="flex items-start gap-2 text-sm text-muted-foreground">
        <Checkbox
          checked={consent}
          onCheckedChange={(v) => {
            setConsent(!!v);
            // Clear the consent error the moment they tick — no reason
            // to keep yelling once they've fixed it.
            if (v && errors.consent) {
              setErrors((e) => ({ ...e, consent: "" }));
            }
          }}
          data-testid="lead-form-consent-checkbox"
        />
        <span>I agree to receive my coupon via WhatsApp/SMS/Email. No spam.</span>
      </label>
      {errors.consent && (
        <p className="text-xs text-destructive" data-testid="lead-form-consent-error">
          {errors.consent}
        </p>
      )}

      {serverError && (
        <p className="text-sm text-destructive" data-testid="lead-form-server-error">
          {serverError}
        </p>
      )}

      <Button
        type="submit"
        className="w-full h-12 rounded-full font-semibold"
        style={
          primaryColor
            ? { backgroundColor: primaryColor, color: "#fff" }
            : undefined
        }
        disabled={submitting}
        data-testid="lead-form-submit-button"
      >
        {submitting ? (
          <span className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Please wait…
          </span>
        ) : (
          // Game-type-aware CTA copy. Spin Wheel stays "Spin the Wheel!"
          // for continuity; Scratch Card gets "Reveal My Prize!" which
          // matches the mental model of scratching a card to reveal.
          gameType === "scratch_card" ? "Reveal My Prize!" : "Spin the Wheel!"
        )}
      </Button>
    </form>
  );
};

const OtpStep = ({
  email,
  cooldownStart,
  onBack,
  onVerify,
  onResend,
  submitting,
  serverError,
  primaryColor,
  gameType,
}) => {
  const [code, setCode] = useState("");
  const [cooldown, setCooldown] = useState(cooldownStart || 60);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submit = (e) => {
    e?.preventDefault();
    if (code.length !== 6) return;
    onVerify(code);
  };

  const resend = async () => {
    setResending(true);
    try {
      const next = await onResend();
      setCooldown(Number(next?.cooldown_seconds) || 60);
      setCode("");
    } finally {
      setResending(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 pt-2" data-testid="lead-otp-step">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        data-testid="lead-otp-back"
      >
        <ArrowLeft className="h-3 w-3" /> Back
      </button>
      <div>
        <p className="text-sm">
          We sent a 6-digit code to{" "}
          <strong className="break-all">{email}</strong>. Enter it below to{" "}
          {gameType === "scratch_card" ? "reveal your prize." : "spin."}
        </p>
      </div>
      <div className="flex justify-center">
        <InputOTP
          maxLength={6}
          value={code}
          onChange={(v) => setCode(v.replace(/\D/g, "").slice(0, 6))}
          inputMode="numeric"
          autoFocus
        >
          <InputOTPGroup>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <InputOTPSlot key={i} index={i} data-testid={`lead-otp-slot-${i}`} />
            ))}
          </InputOTPGroup>
        </InputOTP>
      </div>
      {serverError && (
        <p className="text-sm text-destructive text-center" data-testid="lead-otp-error">
          {serverError}
        </p>
      )}
      <Button
        type="submit"
        className="w-full h-12 rounded-full font-semibold"
        style={
          primaryColor
            ? { backgroundColor: primaryColor, color: "#fff" }
            : undefined
        }
        disabled={submitting || code.length !== 6}
        data-testid="lead-otp-verify"
      >
        {submitting ? (
          <span className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Verifying…
          </span>
        ) : (
          gameType === "scratch_card" ? "Verify and Reveal" : "Verify and Spin"
        )}
      </Button>
      <div className="text-center text-xs text-muted-foreground">
        {cooldown > 0 ? (
          <span data-testid="lead-otp-cooldown">
            Resend available in {cooldown}s
          </span>
        ) : (
          <button
            type="button"
            onClick={resend}
            disabled={resending}
            className="underline hover:text-foreground"
            data-testid="lead-otp-resend"
          >
            {resending ? "Sending…" : "Resend code"}
          </button>
        )}
      </div>
    </form>
  );
};

export const LeadFormModal = ({
  open,
  onOpenChange,
  requireEmail = true,
  customFields = [],
  primaryColor,
  submitting = false,
  serverError = "",
  // NEW props to run the whole 2-step flow inside this modal.
  otpRequired = false,
  otpStep = false, // parent flips to true after form submit + OTP sent
  otpEmail = "",
  otpCooldown = 60,
  onSubmit, // form submit — parent decides "spin now" vs "send OTP"
  onOtpVerify, // called with the 6-digit code
  onOtpResend, // called to re-send; returns { cooldown_seconds } if any
  // Game-type awareness — drives sub-line + CTA + OTP copy so a
  // Scratch Card visitor never sees "spin"-flavoured microcopy.
  gameType = "spin_wheel",
}) => {
  const isScratch = gameType === "scratch_card";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // max-h + inner scroll keeps the whole popup visible above the
        // mobile keyboard on iOS Safari + Android Chrome. Radix already
        // handles scroll-lock on the body, but the DIALOG itself needs
        // an explicit `overflow-y:auto` for the input row to scroll into
        // view as the OS focuses it.
        className="max-w-md p-0 overflow-hidden max-h-[92vh] flex flex-col"
        data-testid="lead-form-modal"
      >
        <div className="px-6 pt-6">
          <DialogHeader>
            <DialogTitle data-testid="lead-form-title">
              {otpStep ? "Verify your email" : "Almost there!"}
            </DialogTitle>
            <DialogDescription>
              {otpStep
                ? "Enter the 6-digit code we just emailed you."
                : isScratch
                  ? "Fill your details to scratch and win."
                  : "Fill your details to spin and win."}
            </DialogDescription>
          </DialogHeader>
        </div>
        <div
          className="px-6 pb-6 pt-2 overflow-y-auto"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {otpStep ? (
            <OtpStep
              email={otpEmail}
              cooldownStart={otpCooldown}
              onBack={() => onOpenChange(false)}
              onVerify={onOtpVerify}
              onResend={onOtpResend}
              submitting={submitting}
              serverError={serverError}
              primaryColor={primaryColor}
              gameType={gameType}
            />
          ) : (
            <InternalLeadForm
              requireEmail={requireEmail || otpRequired}
              customFields={customFields}
              primaryColor={primaryColor}
              submitting={submitting}
              serverError={serverError}
              onSubmit={onSubmit}
              gameType={gameType}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LeadFormModal;
